// Libraries
import React, { useState, useEffect, useRef } from 'react'
import { Formik, Form } from 'formik'
import PropTypes from 'prop-types'
import { Modal, Button } from 'antd'
// Components
import Select from '../../../Base/Forms/Select'
import { Tags } from '../../../EditProfile/Tags'
// Styled Components
import { WrapperInputSkills } from '../../../EditProfile/Modals/styles'
import { ErrorText } from '../../../EditProfile/Inputs/styles'
import { ButtonContainer } from '../../../Base/Forms/styles'
// Hooks
import { useGetToken } from '../../../../hooks/useGetToken'
import { useInputValue } from '../../../../hooks/useInputValue'
import { useSave } from '../../../../Context/saveContext'
// Api
import { epagAPI } from '../../../../API/epagAPI'
// Constants
import { Text } from '../../../../constants/TextConstants'
import { MODELS_GENERIC_MODAL } from '../../../../constants/modals'
import { updateSkillsByMode, tagsMapper, removeSkillAndUpdateSaved } from '../../../../utils/utils'
import levels from '../../../../constants/levels'
import levelsTooltip from '../../../../constants/levelsTooltip'
import { USE_DEFINED_SKILL_SCHEMA } from './schema'
// Utils
import { AddNotFound } from '../../../EditProfile/AddNotFound'

const withoutSkills = ['N/A']
const INITIAL_FORM_VALUES = { skill: null, level: null }

/**
 * This component uses two Selectors by choosing the skill first and then the level of the
 * selected skill.
 */

const UserDefinedSkill = ({
  open,
  setModal,
  setModelProfile,
  title,
  initialState = [],
  mode,
  subtitle,
  skillName,
  applyLevel = true,
  threshold = 0
}) => {
  const { token } = useGetToken()
  const ErrorRequest = useInputValue(false)
  const [skillList, setValueSkillList] = useState([])
  const [currentSkills, setCurrentSkills] = useState([])
  const { setIsSaved } = useSave()
  const [schema, setSchema] = useState(null)
  const formikRef = useRef()

  useEffect(() => {
    const updatedSchema = USE_DEFINED_SKILL_SCHEMA(initialState, currentSkills)
    setSchema(updatedSchema)
  }, [currentSkills, initialState])

  useEffect(() => {
    setCurrentSkills(initialState)
  }, [initialState])

  useEffect(() => {
    if (token !== null && open === true) {
      epagAPI
        .GetSkills(skillName, token)
        .then((response) => {
          if (response.length > 0) {
            const list = updateSkillsByMode(mode, response[0], withoutSkills)
            setValueSkillList(
              list.sort(function (a, b) {
                return a.toLowerCase().localeCompare(b.toLowerCase())
              })
            )
            ErrorRequest.setErrorText('')
            ErrorRequest.setError(false)
          } else {
            setValueSkillList(['No Skills'])
          }
        })
        .catch((error) => {
          ErrorRequest.setErrorText(error.message)
          ErrorRequest.setError(true)
        })
    }
  }, [open, token])

  const onSubmit = () => {
    setModelProfile((prevState) => {
      let copyState = { ...prevState }
      const profileMode = MODELS_GENERIC_MODAL[mode]
      profileMode ? (copyState[MODELS_GENERIC_MODAL[mode]] = currentSkills) : (copyState = currentSkills)
      return copyState
    })
    setIsSaved(false)
    setModal(false)
  }

  const onCancel = () => {
    formikRef?.current?.resetForm()
    setCurrentSkills(initialState)
    setModal(false)
  }

  const setSkillLevelState = (newSkill) => {
    setCurrentSkills((prevState) => [...prevState, newSkill])
  }

  const onChangeLevel = (value, skill) => {
    const newSkill = {
      skill_name: skill,
      level: value
    }
    setSkillLevelState(newSkill, true)
  }

  const handleRemoveSkill = (index) => {
    const updatedSkills = removeSkillAndUpdateSaved(index, currentSkills, initialState, setIsSaved)
    setCurrentSkills(updatedSkills)
  }

  const getOptionsFromArray = (array, tooltips = [], optionToBeFilter = []) =>
    array
      ? array
        .filter((item) => !optionToBeFilter.find((e) => e.skill_name === item))
        .map((item) => ({ value: item, label: item, tooltipText: tooltips[item] }))
      : []

  const onDragTags = (tags) => {
    const transformedTags = tags.map(({ name, level }) => ({
      skill_name: name,
      level
    }))
    setCurrentSkills(transformedTags)
  }

  return (
    <Modal
      okText={Text.Add}
      title={title}
      open={open}
      footer={null}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}>
      <Formik
        initialValues={INITIAL_FORM_VALUES}
        validationSchema={schema}
        initialTouched={{ skill: true, level: true }}
        onSubmit={(values, { resetForm }) => {
          onSubmit(values)
          resetForm()
        }}
        innerRef={formikRef}>
        {({ isValid, values, resetForm, handleSubmit }) => {
          return (
            <Form>
              <WrapperInputSkills spaceStyle={applyLevel ? 'space-around' : ''}>
                <Select
                  name='skill'
                  showSearch
                  label={subtitle}
                  notFoundContent={<AddNotFound />}
                  options={getOptionsFromArray(skillList, [], currentSkills)}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
                {!!applyLevel && (
                  <Select
                    name='level'
                    label='Level'
                    options={getOptionsFromArray(levels, levelsTooltip)}
                    onChange={(value) => {
                      onChangeLevel(value, values.skill)
                      resetForm()
                    }}
                    disabled={values && !values.skill}
                  />
                )}
              </WrapperInputSkills>
              <Tags
                data={tagsMapper(currentSkills)}
                isDraggable={false}
                categoryName={skillName}
                threshold={threshold}
                onDragTags={onDragTags}
                removeItemFunc={(index) => {
                  resetForm()
                  handleRemoveSkill(index)
                }}
              />
              {!!ErrorRequest.ErrorValue && <ErrorText>{ErrorRequest.ErrorText}</ErrorText>}
              <ButtonContainer>
                <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                  Cancel
                </Button>
                <Button type='primary' onClick={() => handleSubmit()} disabled={!isValid}>
                  Add
                </Button>
              </ButtonContainer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

UserDefinedSkill.propTypes = {
  open: PropTypes.bool,
  setModal: PropTypes.func,
  setModelProfile: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  initialState: PropTypes.array,
  mode: PropTypes.string,
  subtitle: PropTypes.string,
  skillName: PropTypes.string,
  applyLevel: PropTypes.bool,
  allowAdd: PropTypes.bool,
  threshold: PropTypes.number
}

export default UserDefinedSkill
